<template>
  <div class="bg-white border" style="border-radius:14px; border-color:#e9ecef !important;">
    <div class="p-4">

      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between pb-3 mb-3" style="border-bottom:1px solid #e9ecef;">
        <h3 class="h6 font-weight-bold text-dark mb-0">Active alerts</h3>
        <div class="d-flex" style="gap:8px;">
          <span v-if="criticalCount > 0" class="badge badge-danger px-2 py-1" style="font-size:11px;border-radius:6px;">
            {{ criticalCount }} critical
          </span>
          <span v-if="highCount > 0" class="badge badge-secondary px-2 py-1" style="font-size:11px;border-radius:6px;">
            {{ highCount }} high
          </span>
        </div>
      </div>

      <!-- Alert list -->
      <div v-if="alerts.length > 0">
        <div
          v-for="alert in sortedAlerts"
          :key="alert.type + alert.triggered_at"
          class="d-flex p-3 mb-3"
          :class="getCardBg(alert.severity)"
          style="border-radius:8px; gap:12px;"
        >
          <!-- Icon -->
          <div
            class="d-flex align-items-center justify-content-center bg-dark text-light flex-shrink-0"
            style="width:38px;height:38px;border-radius:8px;font-size:15px;"
          >
            <i :class="getAlertIcon(alert.type)"></i>
          </div>

          <!-- Content -->
          <div class="flex-grow-1" style="min-width:0;">

            <!-- Type + severity badge -->
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-1" style="gap:6px;">
              <span class="font-weight-bold text-dark" style="font-size:13px;">
                {{ formatAlertType(alert.type) }}
              </span>
              <span
                class="badge px-2 py-1"
                :class="getSeverityBadge(alert.severity)"
                style="font-size:10px;letter-spacing:0.5px;text-transform:uppercase;border-radius:5px;"
              >
                {{ alert.severity }}
              </span>
            </div>

            <!-- Message -->
            <p class="text-secondary mb-2" style="font-size:13px;line-height:1.5;margin:0 0 8px;">
              {{ alert.message }}
            </p>

            <!-- Recommended action -->
            <div
              v-if="alert.recommended_action"
              class="d-flex align-items-start p-2 mb-2 bg-light"
              style="border-radius:6px;gap:8px;"
            >
              <i class="fas fa-lightbulb text-secondary mt-1" style="font-size:12px;flex-shrink:0;"></i>
              <span class="text-dark" style="font-size:12px;font-weight:500;">{{ alert.recommended_action }}</span>
            </div>

            <!-- Toggle details -->
            <div v-if="alert.details" class="mb-2">
              <button
                @click="toggleDetails(alert)"
                class="btn btn-sm btn-light border text-secondary d-inline-flex align-items-center"
                style="font-size:11px;font-weight:600;border-radius:6px;gap:5px;"
              >
                {{ expandedAlerts.includes(alert.type) ? 'Hide' : 'Show' }} Details
                <i :class="expandedAlerts.includes(alert.type) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              </button>

              <div
                v-if="expandedAlerts.includes(alert.type)"
                class="mt-2 p-2 bg-light border"
                style="border-radius:6px;overflow-x:auto;"
              >
                <pre class="mb-0 text-secondary" style="font-size:11px;">
                    <div >
                        <ul class="list-group">
                            <li v-for="item in alert.details?.products ">{{ item.name }}</li>
                        </ul>
                    </div>
                </pre>
              </div>
            </div>

            <!-- Footer: time + dismiss -->
            <div
              class="d-flex align-items-center justify-content-between pt-2"
              style="border-top:1px solid rgba(0,0,0,0.06);"
            >
              <span class="text-secondary" style="font-size:11px;">
                <i class="far fa-clock mr-1"></i>{{ formatTime(alert.triggered_at) }}
              </span>
              <button
                @click="dismissAlert(alert)"
                class="btn btn-sm btn-light border-0 text-secondary d-inline-flex align-items-center"
                style="font-size:11px;font-weight:600;border-radius:6px;gap:4px;"
              >
                <i class="fas fa-times"></i> Dismiss
              </button>
            </div>

          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="text-center py-5">
        <i class="fas fa-check-circle text-secondary mb-3" style="font-size:40px;display:block;"></i>
        <p class="font-weight-bold text-dark mb-1" style="font-size:15px;">No active alerts</p>
        <span class="text-secondary" style="font-size:13px;">All systems running smoothly</span>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'AlertsPanel',

  props: {
    alerts: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      expandedAlerts: []
    };
  },

  computed: {
    sortedAlerts() {
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      return [...this.alerts].sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
    },

    criticalCount() {
      return this.alerts.filter(a => a.severity === 'critical').length;
    },

    highCount() {
      return this.alerts.filter(a => a.severity === 'high').length;
    }
  },

  methods: {
    formatAlertType(type) {
      return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    getAlertIcon(type) {
      const icons = {
        high_refund_rate: 'fas fa-undo-alt',
        excessive_discount: 'fas fa-tags',
        high_void_rate: 'fas fa-ban',
        low_stock: 'fas fa-box-open',
        critical_stockout: 'fas fa-exclamation-triangle',
        excessive_waste: 'fas fa-trash',
        cashier_discount_abuse: 'fas fa-user-times',
        long_open_orders: 'fas fa-clock',
        sales_anomaly: 'fas fa-chart-line'
      };
      return icons[type] || 'fas fa-exclamation-circle';
    },

    getCardBg(severity) {
      // critical/high get a very subtle dark tint; others get light grey
      return severity === 'critical' || severity === 'high' ? 'bg-light' : 'bg-light';
    },

    getSeverityBadge(severity) {
      return severity === 'critical' ? 'badge-dark' : 'badge-secondary';
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      const diff = Math.floor((new Date() - date) / 60000);
      if (diff < 1) return 'Just now';
      if (diff < 60) return `${diff} min ago`;
      if (diff < 1440) return `${Math.floor(diff / 60)} hours ago`;
      return date.toLocaleDateString();
    },

    toggleDetails(alert) {
      const index = this.expandedAlerts.indexOf(alert.type);
      if (index > -1) {
        this.expandedAlerts.splice(index, 1);
      } else {
        this.expandedAlerts.push(alert.type);
      }
    },

    dismissAlert(alert) {
      this.$emit('dismiss', alert.type);
    }
  }
};
</script>
